/**
 * MONSTER TOWN — VISIT YOUR BROTHER'S TOWN. VISIT CREW (src/visit/**).
 *
 * "Walk into the other slot's town as a VISITOR: look, don't touch. Nothing can
 * be moved, deleted, or broken — a visitor cannot damage a town, by design, and
 * this must be guaranteed at the save layer." (EXPANSION-BRIEF §8.)
 *
 * ===========================================================================
 *  WHERE THE GUARANTEE ACTUALLY LIVES — READ THIS BEFORE CHANGING ANYTHING
 * ===========================================================================
 * It is NOT here. It is in `src/core/save.ts`, and that is the whole point:
 *
 *   - `save.beginVisit(slot)` HYDRATES from the other slot's bytes without ever
 *     pointing `this.slot` at it.
 *   - `commit()` refuses outright while a visit is open. No bytes reach ANY
 *     slot, not the visited one and not the visitor's own.
 *   - `setSlot`/`useSlot` refuse, so the active-slot pointer cannot drift onto
 *     the visited town and start autosaving into it after the visit.
 *   - There is no `write(slot, …)` taking a slot argument anywhere in
 *     `SaveManager`. "Write the other town" is not a code path that exists to be
 *     reached by accident — which is a different and much stronger statement
 *     than "the button is hidden".
 *
 * THIS FILE DELIBERATELY DOES NOT ADD A SECOND, SOFTER GUARD. In particular it
 * does NOT force the app out of build mode on every `app:modeChanged`, and the
 * temptation to is worth spelling out: a snap-back guard would make it
 * IMPOSSIBLE for a QA tool to place a piece during a visit, so the save layer's
 * refusal would never be exercised and `save-migration`'s "every write is
 * refused and no slot's bytes change" would start passing because nothing ever
 * got dirty. A guard that hides the thing it is supposed to be proving is worse
 * than no guard. What this crew does instead is REMOVE THE CONTROLS a kid could
 * reach (see `visit.css` — no hammer, no bin, no drawer, no rail), so there is
 * no path in for a finger, while the tools can still poke as hard as they like
 * and watch the bytes refuse to move.
 *
 * WHAT THIS FILE OWNS
 *   - the way in and the way out, and the promise that neither can strand a kid
 *   - "you visit as YOU": their town, your monster
 *   - souvenirs — a photo taken in their town is held in memory and filed into
 *     YOUR OWN album only after you get home, because while you are a guest
 *     there is nowhere safe to write it
 */

import { bus } from '../core/events'
import { noteVisit, otherSlot } from '../core/daySave'
import { save } from '../core/save'
import type { MonsterConfig, SaveSlot, SceneId } from '../core/types'
import { app, type GameContext } from '../app/context'
import { setPlayerLook } from '../monster/player'

/** A picture brought home from a visit. Bytes live for the session only. */
export interface Souvenir {
  /** Stable id filed into the `visit` save section. Never image bytes. */
  readonly id: string
  /** Whose town it is a picture of. */
  readonly slot: SaveSlot
  /** data: URL, this session only. `null` for one restored from a save. */
  readonly url: string | null
  readonly at: number
}

export type Fader = (dir: 'out' | 'in') => Promise<void>

function frames(n: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let left = Math.max(1, n)
    const step = (): void => {
      left--
      if (left <= 0) resolve()
      else requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  })
}

class VisitState {
  private ctx: GameContext | null = null
  private fader: Fader | null = null
  /** Photos taken while a guest, waiting for us to get home. */
  private pending: Souvenir[] = []
  /** Everything in the album this session, newest last. */
  private album: Souvenir[] = []
  private busy = false
  private seq = 0

  attach(ctx: GameContext): void {
    this.ctx = ctx
  }

  setFader(f: Fader | null): void {
    this.fader = f
  }

  /** The slot that is not ours. Two kids, so this is total. */
  get other(): SaveSlot {
    return otherSlot(save.visiting ? (save.visitHostSlot ?? save.activeSlot) : save.activeSlot)
  }

  /** Is there a town over there to walk into yet? */
  get canVisit(): boolean {
    return !save.visiting && !this.busy && save.hasSave(this.other)
  }

  get visiting(): boolean {
    return save.visiting
  }

  /** Whose town are we standing in, or null. */
  get hostOf(): SaveSlot | null {
    return save.visitSlot
  }

  souvenirs(): readonly Souvenir[] {
    return this.album
  }

  // -- the way in ----------------------------------------------------------

  /**
   * Walk in. Always ends either inside their town or exactly where it started —
   * there is no half-open state a kid can be left in.
   */
  async enter(): Promise<boolean> {
    if (save.visiting) return true
    if (this.busy) return false
    const ctx = this.ctx
    const slot = this.other
    if (ctx === null || !save.hasSave(slot)) return false
    this.busy = true
    try {
      /**
       * COMMIT OUR OWN TOWN BEFORE WE LEAVE IT, explicitly.
       *
       * `beginVisit()` flushes too, but its flush can be DEFERRED by a scene
       * transition guard, and the transition we may be about to do below is
       * exactly such a guard. Everything a kid built in the last 900 ms of
       * autosave debounce would then only exist in memory — and `endVisit()`
       * reloads from disk, so it would be gone. Flush first, while nothing is
       * in flight.
       */
      save.flush('visit:before')

      // A guest arrives OUTSIDE. If the kid tapped the gate while standing in
      // their own kitchen, walk them out first — otherwise the other town's
      // save hydrates underneath them and they are suddenly in somebody else's
      // kitchen with no memory of a door.
      const outside = this.exteriorScene()
      const activeId = ctx.world.activeSceneId
      if (outside !== null && activeId !== outside) {
        await ctx.world.transitionTo(outside, { instant: true })
      }

      const mine: MonsterConfig = { ...app.monster }
      await this.fade('out')

      const opened = save.beginVisit(slot)
      if (!opened) {
        await this.fade('in')
        return false
      }

      // Arrive in look-around mode. ONE call, at the moment of arrival — not a
      // standing guard. See the header.
      app.setMode('play')

      /**
       * YOU VISIT AS YOU. `beginVisit` hydrated every section from their bytes,
       * including the monster, so without this the kid would be walking around
       * as their brother. `setPlayerLook` calls `save.request()`, which during a
       * visit sets the dirty flag and nothing else: `commit()` refuses, and
       * `endVisit()` clears the flag and reloads from disk. No bytes move.
       */
      setPlayerLook(mine, 'visit')

      await frames(2)
      ctx.camera.releaseHold()
      ctx.camera.lookAtPoint(app.playerPos, true)
      await frames(2)
      await this.fade('in')
      bus.emit('audio:cue', { cue: 'door_open' })
      return true
    } finally {
      this.busy = false
    }
  }

  // -- the way out ---------------------------------------------------------

  /**
   * Go home. `save.endVisit()` ALWAYS reloads the host's own slot from disk, so
   * nothing seen or poked in the other kid's world can survive into this kid's
   * save — including anything a tool placed while it was proving that point.
   */
  async leave(): Promise<boolean> {
    if (!save.visiting) return false
    if (this.busy) return false
    this.busy = true
    try {
      await this.fade('out')
      save.endVisit()
      app.setMode('play')
      await frames(2)

      // NOW we are allowed to write again, so the souvenirs go in the album.
      this.fileSouvenirs()

      const ctx = this.ctx
      if (ctx !== null) {
        ctx.camera.releaseHold()
        ctx.camera.lookAtPoint(app.playerPos, true)
      }
      await frames(2)
      await this.fade('in')
      bus.emit('audio:cue', { cue: 'door' })
      return true
    } finally {
      this.busy = false
    }
  }

  // -- souvenirs -----------------------------------------------------------

  /**
   * A picture of their town, taken while standing in it. Held in memory with a
   * stable id; the id is written into OUR OWN `visit` save section the moment we
   * are home and writing is legal again.
   */
  keepSouvenir(url: string): Souvenir | null {
    const slot = save.visitSlot
    if (slot === null) return null
    const s: Souvenir = {
      id: `visit-${slot}-${Date.now().toString(36)}-${this.seq++}`,
      slot,
      url,
      at: Date.now(),
    }
    this.pending.push(s)
    this.album.push(s)
    while (this.album.length > 12) this.album.shift()
    return s
  }

  private fileSouvenirs(): void {
    if (this.pending.length === 0) return
    for (const s of this.pending) noteVisit(s.id)
    this.pending.length = 0
  }

  /** Album ids already on disk from earlier sessions, newest last. */
  restoreAlbumIds(ids: readonly string[]): void {
    const have = new Set(this.album.map((s) => s.id))
    const older: Souvenir[] = []
    for (const id of ids) {
      if (have.has(id)) continue
      const slot: SaveSlot = id.includes('-b-') ? 'b' : 'a'
      older.push({ id, slot, url: null, at: 0 })
    }
    this.album = [...older, ...this.album].slice(-12)
  }

  // -- internals -----------------------------------------------------------

  private exteriorScene(): SceneId | null {
    const ctx = this.ctx
    if (ctx === null) return null
    const active = ctx.world.activeSceneId
    if (active !== null && ctx.world.get(active)?.def.kind === 'exterior') return active
    for (const id of ctx.world.ids()) {
      if (ctx.world.get(id)?.def.kind === 'exterior') return id
    }
    return null
  }

  private async fade(dir: 'out' | 'in'): Promise<void> {
    const f = this.fader
    if (f === null) return
    await f(dir)
  }
}

export const visit = new VisitState()
